import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {AutoComplete, Checkbox, ConfigProvider, Input, Space, Tag, Button, Menu, Divider, Radio, Typography, Collapse, Tabs, notification, Popconfirm, Alert, List, Modal, Tooltip, message} from "antd";
import api from "../api";
import SearchOutlined from "@ant-design/icons/lib/icons/SearchOutlined";
import {
    BulbOutlined,
    CaretDownOutlined, DownOutlined,
    FolderOutlined,
    LinkOutlined,
    LoadingOutlined,
    SettingOutlined,
    TagOutlined,
    ClearOutlined,
    FileSearchOutlined,
    FilterOutlined,
    CodeOutlined,
    CheckSquareOutlined,
    SaveOutlined,
    DeleteOutlined,
    EditOutlined,
} from "@ant-design/icons";
import {useAssetsDispatch} from "../../contexts/AssetsContext";
import {useLoadedAssetsDispatch} from "../../contexts/LoadedAssetsContext";
import {useFilters} from "../helpers/useFilters";
import queryString from "query-string";
import {SessionContext} from "../../contexts/SessionContext";
import {
    BrowserView,
    MobileView,
    isBrowser,
} from "device-detect";
import {GlobalHotKeys} from "react-hotkeys";
import {useNavigate} from "react-router-dom-v5-compat";
import useShowAsset from "../helpers/useShowAsset";
import {isMobile} from "../../plugins/device-detect";
import {AbilityContext} from "../helpers/Can";
import {useOrgPath} from "../helpers/OrgNavLink";
import {useTranslation} from "react-i18next";
import VerticalSpace from "@/components/helpers/VerticalSpace";
import { AntdConfig, Query, Builder, Utils as QbUtils } from '@react-awesome-query-builder/antd';
import '@react-awesome-query-builder/ui/css/styles.css';
import {useStorageState} from "react-storage-hooks";
import HelpPopover from "../HelpPopover";
import {
    FILENAME_MATCH_MODES,
    normalizeGeneralTextSearchMatching,
    parseFilenameSearchTerms,
} from "./siteSearchHelpers";

const InitialConfig = AntdConfig;
const initQuery = {"id": QbUtils.uuid(), "type": "group"};

export default ({inputRef: externalInputRef})=> {
    const { t } = useTranslation();
    const {state: sessionState, dispatch: sessionDispatch} = useContext(SessionContext);
    const {currentOrg} = sessionState;

    const [searchLoading, setSearchLoading] = useState()

    const [searchOptions, setSearchOptions] = useState([])

    const lastSearchValue = useRef();
    const currentInputValue = useRef('');
    const debouncedSearch = useCallback(_.debounce((value)=>{
        console.log('searching:', value)
        lastSearchValue.current = value;

        if(value === '') return setSearchOptions([]);

        setSearchLoading(true);

        api('/api/search', {params: {q: value}}).then(res => {
            setSearchLoading(false);
            // console.log(value, res.data)
            if(value != lastSearchValue.current || res.data.error) return;
            console.log('searched for: ', value)

            const newOptions = [
                {
                    label: <><SearchOutlined/> {t('search-all','Search All')}...</>, value: 'all'
                },
                {
                    label: t('tags','Tags'),
                    options: res.data.tags.map(tag => {
                        return {
                            value: `tag-${tag.id}`,
                            label: (
                                <Space direction={'horizontal'}>
                                    <Tag icon={<TagOutlined/>}>
                                        {tag.name}
                                    </Tag>
                                </Space>
                            )
                        }
                    })
                },
                {
                    label: t('collections','Collections'),
                    options: res.data.collections.map(col => {
                        return {
                            value: `collection-${col.id}`,
                            label: (
                                <Space direction={'horizontal'}>
                                    <FolderOutlined/>
                                    {col.path_names.join(' / ')}
                                </Space>
                            )
                        }
                    })
                },
                {
                    label: t('lightboxes','Lightboxes'),
                    options: res.data.lightboxes.map(col => {
                        return {
                            value: `lightbox-${col.id}`,
                            label: (
                                <Space direction={'horizontal'}>
                                    <FolderOutlined/>
                                    {col.path_names.join(' / ')}
                                </Space>
                            )
                        }
                    })
                },
                {
                    label: t('storage-folders','Storage Folders'),
                    options: res.data.storage_folders.map(sf => {
                        return {
                            value: `storage_folder-${sf.id}`,
                            label: (
                                <Space direction={'horizontal'}>
                                    <FolderOutlined/>
                                    {sf.path_names.join(' / ')}
                                </Space>
                            )
                        }
                    })
                },
                {
                    label: t('links','Links'),
                    options: res.data.links.map(link => {
                        return {
                            value: `link-${link.id}`,
                            label: (
                                <Space direction={'horizontal'}>
                                    <LinkOutlined/> {link.url}
                                </Space>
                            )
                        }
                    })
                },
                {
                    label: t('assets','Assets'),
                    options: res.data.assets.map(asset => {
                        return {
                            value: `asset-${asset.id}`,
                            label: (
                                <Space direction={'horizontal'}>
                                    {!!asset.thumb_url?.length && (
                                        <img src={asset.preview_image_url || asset.thumb_url} style={{ width: 50, height: 50, objectFit: 'scale-down'}}/>
                                    )}
                                    {asset.filename}
                                    |
                                    {asset.description}
                                </Space>
                            )
                        }
                    }),
                },
            ];

            setSearchOptions(newOptions.filter(opt => opt.value === 'all' || (opt.options && opt.options.length)))
        });
    }, 250), [])

    const search = useCallback((value) => {
        currentInputValue.current = value;
        debouncedSearch(value);
    }, [debouncedSearch]);

    const navigate = useNavigate();

    const {setFilters, filters, replaceFilters} = useFilters();

    const showAsset = useShowAsset()

    const getPath = useOrgPath()

    const gotoExplore = ()=>{
        setTimeout(()=>{
            if(!location.pathname.match(/explore/)) {
                location.pathname = getPath(`/explore`)
                navigate(location);
            }
        }, 100)
    }

    const selectSearchOption = (value, option)=> {
        console.log(value, option, searchValue)

        if(value === 'all') {
            // Use currentInputValue.current (tracks typed text synchronously) because:
            // 1. searchValue may have been changed to 'all' by AutoComplete's onChange
            // 2. lastSearchValue.current may be stale if the user typed and hit Enter before the debounce fired
            const actualSearchValue = currentInputValue.current || searchValue;
            setFilters(false, {q: actualSearchValue})
            setSearchValue(actualSearchValue)
            gotoExplore()
            return;
        }

        const id = parseInt(value.match(/-(.+?)$/)[1])

        if(value.match(/^asset/)) {
            showAsset(id)
        } else if(value.match(/^storage_folder/)) {
            api(`/api/storage_folders/${id}`).then(res => {
                navigate(getPath(`/explore/folders/${res.data.slug}`));
            })

        } else if(value.match(/^collection/)) {
            api(`/api/collections/${id}`).then(res => {
                navigate(getPath(`/explore/collections/${res.data.slug}`));
            })

        } else if(value.match(/^lightbox/)) {
            api(`/api/lightboxes/${id}`).then(res => {
                navigate(getPath(`/explore/projects/${res.data.slug}`));
            })
        } else if(value.match(/^tag/)) {
            api(`/api/tags/${id}`).then(res => {
                setFilters(false, {tags: res.data.name})
                gotoExplore()
            })
        } else if(value.match(/^link/)) {
            api(`/api/links/${id}`).then(res => {
                setFilters(false, {link: res.data.url})
                gotoExplore()
            })
        }

        setSearchValue('');
    }

    const q = queryString.parse(location.search, {arrayFormat: 'bracket'}).q;
    const text_q = queryString.parse(location.search, {arrayFormat: 'bracket'}).text_q;
    const [searchValue, setSearchValue] = useState(text_q || q);
    if (!currentInputValue.current && (text_q || q)) currentInputValue.current = text_q || q;

    // Modal state
    const [searchModalVisible, setSearchModalVisible] = useState(false);
    const [modalSearchValue, setModalSearchValue] = useState('');

    // Text Search options state (independent from header search)
    const [textSearchScope, setTextSearchScope] = useState('shown'); // 'shown' or 'all'
    const [textSearchMatching, setTextSearchMatching] = useState('contains'); // 'contains', 'exact', 'starts', 'ends'
    const [textSearchMode, setTextSearchMode] = useState('all'); // 'all' or 'any' (for "Has All Terms" vs "Has Any Term")

    // Advanced Search State (from SearchModalButton)
    const [customMetaFields, setCustomMetaFields] = useState([]);

    // Auto Keywords include/exclude toggle
    const [autoKeywordsMode, setAutoKeywordsMode] = useState(
        currentOrg?.omit_auto_tag_results ? 'exclude' : 'include'
    );

    // Default field selections - all enabled by default (search all fields)
    const [selectedFields, setSelectedFields] = useState({
        // Content and Tags
        'ocr_content': true,
        'transcript': true,
        'notes': true,
        'tag_text': true,
        'auto_tag_text': true,
        'person_tag_names': true,
        'face_names': true,
        'event': true,
        'iptceventid': true,

        // Ownership/Rights
        'creator_tag_text': true,
        'creditline': true,
        'iptcsource': true,
        'iptcrights': true,
        'usageterms': true,
        'instructions': true,

        // Location
        'country': true,
        'state': true,
        'city': true,
        'sublocation': true,

        // Description
        'description': true,
        'title': true,
        'headline': true,
        'extendeddescription': true,
        'descriptionwriter': true,
        'alttext': true,
        'tag_description': true,
        'persondescription': true,
        'productdescription': true,

        // Artwork/Object
        'aotitle': true,
        'aocontributiondescription': true,
        'aocontentdescription': true,
        'aophysicaldescription': true,
    });
    const [selectedCustomFields, setSelectedCustomFields] = useState({});

    // Empty/Full Fields tab state
    const [selectedEmptyField, setSelectedEmptyField] = useState(null);
    const [fieldSearchMode, setFieldSearchMode] = useState('empty'); // 'empty' or 'full'


    // Filename Search Tab state
    const [filenameSearchTerm, setFilenameSearchTerm] = useState('');
    const [filenameMatchMode, setFilenameMatchMode] = useState('exact'); // 'exact', 'file_id' or 'partial'
    const [filenameSearchScope, setFilenameSearchScope] = useState('shown'); // 'all' or 'shown'

    // Controlled accordion state (null = all closed)
    const [accordionActiveKey, setAccordionActiveKey] = useState('options');

    // Search Specific Fields accordion — independent from top-level settings
    const [specificFieldsScope, setSpecificFieldsScope] = useState('shown');
    const [specificFieldsMode, setSpecificFieldsMode] = useState('all');

    // Empty/Full Fields accordion scope
    const [emptyFieldSearchScope, setEmptyFieldSearchScope] = useState('shown');

    // Query Builder accordion scope
    const [qbSearchScope, setQbSearchScope] = useState('shown');

    // Query Builder state
    const initConfig = {
        ...InitialConfig,
        fields: {
            created_at: {
                label: t('date-uploaded','Date Uploaded'),
                type: 'datetime',
                defaultOperator: 'between',
            },
            updated_at: {
                label: t('last-update-date','Last Update Date'),
                type: 'datetime',
                defaultOperator: 'between',
            },
            description: {
                label: t('description','Description'),
                type: 'text',
                defaultOperator: 'like',
            },
            ext: {
                label: t('file-extension','File Extension'),
                type: 'multiselect',
                fieldSettings: {
                    listValues: {},
                },
                allowCustomValues: true
            },
            filename: {
                label: t('filename','Filename'),
                type: 'text',
            },
            file_size_mb: {
                label: t('file-size-mb','File Size (MB)'),
                type: 'number',
                defaultOperator: 'between',
                fieldSettings: {min: 0, step: 0.1},
            },
            tag_names: {
                label: t('tags','Tags'),
                type: 'multiselect',
                fieldSettings: {
                    listValues: {},
                },
                allowCustomValues: true
            },
            rating: {
                label: t('rating','Rating'),
                type: 'number',
                defaultOperator: 'between',
                fieldSettings: {min: 0, max:5},
            },
            width: {
                label: t('width','Width'),
                type: 'number',
                defaultOperator: 'between',
                fieldSettings: {min: 0},
            },
            height: {
                label: t('height','Height'),
                type: 'number',
                defaultOperator: 'between',
                fieldSettings: {min: 0},
            },
        }
    };

    // Dynamically add IPTC fields from MetaFields (set by AssetIptc::to_json)
    Object.keys(MetaFields).forEach(f => {
        const meta = MetaFields[f];
        const defaultOperators = ['is_null', 'is_not_null'];
        let operators = [];
        let type = '';
        switch (meta.index_type) {
            case 'boolean':
                type = 'boolean';
                break;
            case 'date':
                type = 'date';
                operators = ['equal', 'not_equal', 'less', 'less_or_equal', 'greater', 'greater_or_equal', 'between', 'not_between'];
                break;
            case 'integer':
                type = 'number';
                operators = ['equal', 'not_equal', 'less', 'less_or_equal', 'greater', 'greater_or_equal', 'between', 'not_between'];
                break;
            case 'keyword':
            case 'text':
            default:
                type = 'text';
                operators = ['equal', 'not_equal', 'like', 'not_like', 'starts_with', 'ends_with'];
        }
        initConfig.fields[meta.index_name || f] = {
            label: meta.name,
            type,
            operators: [...defaultOperators, ...operators],
            defaultOperator: type === 'text' ? 'like' : undefined,
        };
    });

    initConfig.settings.groupActionsPosition = 'bottomLeft';
    initConfig.settings.addRuleLabel = 'Add Search Term';
    initConfig.settings.addGroupLabel = 'Add Group of Search Terms';

    const [queryValue, setQueryValue] = useStorageState(sessionStorage, `query-builder`, initQuery);
    const [qbConfig, setQbConfig] = useState(initConfig);
    const [qbTree, setQbTree] = useState(QbUtils.sanitizeTree(QbUtils.loadTree(queryValue), initConfig).fixedTree);
    const [qbResult, setQbResult] = useState();
    const [qbSearchQueries, setQbSearchQueries] = useState();
    const [qbSavedCount, setQbSavedCount] = useState(0);
    const searchQueryNameInput = useRef();

    // Update search value when URL changes (e.g., from Advanced Search)
    useEffect(() => {
        setSearchValue(text_q || q || '');
    }, [q, text_q]);

    useEffect(()=>{
        if(!q && !text_q) setSearchValue('')
    }, [q, text_q])

    // Initialize Text Search modal state from URL parameters when modal opens
    useEffect(() => {
        if (!searchModalVisible) return;

        setModalSearchValue(filters.text_q || '');
        if (filters.text_search_scope) {
            setTextSearchScope(filters.text_search_scope);
        }
        if (filters.text_search_matching) {
            setTextSearchMatching(normalizeGeneralTextSearchMatching(filters.text_search_matching));
        }
        if (filters.text_search_mode) {
            setTextSearchMode(filters.text_search_mode);
        }

        if (filters.auto_keywords_mode) {
            setAutoKeywordsMode(filters.auto_keywords_mode);
        }

        // Initialize empty/full field selection from URL
        if (filters.search_empty_fields) {
            const fields = Array.isArray(filters.search_empty_fields) ? filters.search_empty_fields : [filters.search_empty_fields];
            if (fields.length > 0) {
                setSelectedEmptyField(fields[fields.length - 1]);
                setFieldSearchMode('empty');
            }
        } else if (filters.search_full_fields) {
            const fields = Array.isArray(filters.search_full_fields) ? filters.search_full_fields : [filters.search_full_fields];
            if (fields.length > 0) {
                setSelectedEmptyField(fields[fields.length - 1]);
                setFieldSearchMode('full');
            }
        }

        // Initialize field selections from URL
        if (filters.included_search_fields && Array.isArray(filters.included_search_fields)) {
            const newSelectedFields = {};
            const newSelectedCustomFields = {};

            filters.included_search_fields.forEach(field => {
                if (field.startsWith('cmf')) {
                    newSelectedCustomFields[field] = true;
                } else if (selectedFields.hasOwnProperty(field)) {
                    newSelectedFields[field] = true;
                }
            });

            // Only update if we have fields to select
            if (Object.keys(newSelectedFields).length > 0 || Object.keys(newSelectedCustomFields).length > 0) {
                setSelectedFields(prev => ({ ...prev, ...newSelectedFields }));
                setSelectedCustomFields(prev => ({ ...prev, ...newSelectedCustomFields }));
            }
        }
    }, [searchModalVisible])

    const inputRef = externalInputRef || useRef();
    const ref = useRef();
    const searchContainerRef = useRef();
    const settingsButtonRef = useRef();
    const keyMap = { FWD_SLASH: '/' }

    const handlers = {
        FWD_SLASH: (e) => {
            e.preventDefault()
            inputRef.current.focus()
        }
    }

    const ability = useContext(AbilityContext);

    // Fetch custom meta fields and auto-check them
    useEffect(() => {
        if (customMetaFields.length > 0) return;

        api.get('/api/custom_meta_fields?owner_type=Asset').then(res => {
            const enabledFields = res.data.filter(cmf => cmf.enabled);
            setCustomMetaFields(enabledFields);
            // Auto-check all custom fields by default
            const newCustomFields = {};
            enabledFields.forEach(cmf => { newCustomFields[`cmf${cmf.number}`] = true; });
            setSelectedCustomFields(prev => ({ ...newCustomFields, ...prev }));
        }).catch(err => {
            console.error('Failed to fetch custom meta fields:', err);
        });
    }, []);

    // Helper functions for field selection
    const toggleAllFields = useCallback((checked) => {
        setSelectedFields(prev => {
            const newFields = {};
            Object.keys(prev).forEach(key => {
                newFields[key] = checked;
            });
            return newFields;
        });

        setSelectedCustomFields(() => {
            const newCustomFields = {};
            customMetaFields.forEach(cmf => {
                newCustomFields[`cmf${cmf.number}`] = checked;
            });
            return newCustomFields;
        });
    }, [customMetaFields]);

    const toggleField = useCallback((fieldName) => {
        setSelectedFields(prev => ({
            ...prev,
            [fieldName]: !prev[fieldName]
        }));
    }, []);

    const toggleCustomField = useCallback((fieldName) => {
        setSelectedCustomFields(prev => ({
            ...prev,
            [fieldName]: !prev[fieldName]
        }));
    }, []);

    const toggleGroupFields = useCallback((groupFieldKeys, checked) => {
        setSelectedFields(prev => {
            const newFields = { ...prev };
            groupFieldKeys.forEach(field => {
                if (newFields.hasOwnProperty(field)) {
                    newFields[field] = checked;
                }
            });
            return newFields;
        });
    }, []);


    const handleFilenameSearch = () => {
        let searchText = filenameSearchTerm.trim();
        if (!searchText) return;

        const filenames = parseFilenameSearchTerms(searchText, filenameMatchMode);

        const searchFilters = {
            text_q: filenames.join('\n'),
            text_search_matching: FILENAME_MATCH_MODES[filenameMatchMode] || 'contains',
            text_search_mode: 'any',
            text_search_scope: filenameSearchScope,
            included_search_fields: ['filename'],
        };

        if (filenameSearchScope === 'all') {
            const pathParts = window.location.pathname.split('/');
            const orgSlug = pathParts[1];
            replaceFilters(searchFilters, orgSlug ? `/${orgSlug}/explore` : null);
        } else {
            setFilters(false, searchFilters);
            gotoExplore();
        }
    };

    const handleEmptyFieldSearch = () => {
        if (!selectedEmptyField) {
            notification.warning({
                message: t('no-field-selected', 'No field selected'),
                description: t('select-field-for-search', 'Please select a field to search')
            });
            return;
        }

        const key = fieldSearchMode === 'empty' ? 'search_empty_fields' : 'search_full_fields';
        const existing = filters[key]
            ? (Array.isArray(filters[key]) ? filters[key] : [filters[key]])
            : [];
        const newValue = existing.includes(selectedEmptyField) ? existing : [...existing, selectedEmptyField];
        const searchFilters = { [key]: newValue };

        if (emptyFieldSearchScope === 'all') {
            const pathParts = window.location.pathname.split('/');
            const orgSlug = pathParts[1];
            replaceFilters(searchFilters, orgSlug ? `/${orgSlug}/explore` : null);
        } else {
            setFilters(false, searchFilters);
            gotoExplore();
        }
    };

    const handleModalSearch = () => {
        // Collect selected fields
        let includedFields = [];

        // Add selected standard fields
        Object.keys(selectedFields).forEach(fieldName => {
            if (selectedFields[fieldName]) {
                includedFields.push(fieldName);
            }
        });

        // Add selected custom fields
        Object.keys(selectedCustomFields).forEach(fieldName => {
            if (selectedCustomFields[fieldName]) {
                includedFields.push(fieldName);
            }
        });

        // Handle auto keywords mode - remove auto_tag_text from field list if excluded
        // (the backend also handles this via auto_keywords_mode param)
        if (autoKeywordsMode === 'exclude') {
            includedFields = includedFields.filter(f => f !== 'auto_tag_text');
        }

        // Trigger the search with Text Search parameters
        if (modalSearchValue?.trim()) {
            const searchFilters = {
                text_q: modalSearchValue,
                text_search_scope: specificFieldsScope,
                text_search_matching: normalizeGeneralTextSearchMatching(textSearchMatching),
                text_search_mode: specificFieldsMode,
                auto_keywords_mode: autoKeywordsMode
            };

            // Only add included_search_fields if some fields are selected
            if (includedFields.length > 0) {
                searchFilters.included_search_fields = includedFields;
            }

            if (specificFieldsScope === 'all') {
                // "All Files" should clear existing filters and navigate to explore root
                const pathParts = window.location.pathname.split('/');
                const orgSlug = pathParts[1];
                replaceFilters(searchFilters, orgSlug ? `/${orgSlug}/explore` : null);
            } else {
                // "Shown Files" should merge with existing filters
                setFilters(false, searchFilters);
                gotoExplore();
            }
        }

        // Close the modal
        setSearchModalVisible(false);
    };

    const handleTopSearch = () => {
        if (!searchValue?.trim()) return;

        const searchFilters = {
            text_q: searchValue,
            text_search_scope: textSearchScope,
            text_search_matching: normalizeGeneralTextSearchMatching(textSearchMatching),
            text_search_mode: textSearchMode,
            auto_keywords_mode: autoKeywordsMode
        };

        if (textSearchScope === 'all') {
            const pathParts = window.location.pathname.split('/');
            const orgSlug = pathParts[1];
            replaceFilters(searchFilters, orgSlug ? `/${orgSlug}/explore` : null);
        } else {
            setFilters(false, searchFilters);
            gotoExplore();
        }
        setSearchModalVisible(false);
    };

    // Render functions for each tab
    const renderFilenameSearch = () => (
        <div style={{padding: '12px 0'}}>
            <Space direction="vertical" style={{width: '100%'}} size="middle">
                <Input.TextArea
                    placeholder={t('enter-filenames-or-ids', 'Enter or paste filenames or file IDs (one per line, or comma/space separated)')}
                    value={filenameSearchTerm}
                    onChange={(e) => setFilenameSearchTerm(e.target.value)}
                    onPressEnter={(e) => {
                        if (e.metaKey || e.ctrlKey) {
                            handleFilenameSearch();
                            setSearchModalVisible(false);
                        }
                    }}
                    rows={4}
                    size="small"
                />

                <Radio.Group value={filenameMatchMode} onChange={(e) => setFilenameMatchMode(e.target.value)} size="small">
                    <Radio value="exact">{t('exact-match', 'Exact Match')}</Radio>
                    <Radio value="file_id">{t('ignore-file-extension', 'Ignore file extension')}</Radio>
                    <Radio value="partial">{t('exact-partial-matches', 'Exact and Partial matches')}</Radio>
                </Radio.Group>

                <Radio.Group value={filenameSearchScope} onChange={(e) => setFilenameSearchScope(e.target.value)} size="small">
                    <Radio value="shown">{t('shown-files', 'Shown files')}</Radio>
                    <Radio value="all">{t('all-files', 'All Files')}</Radio>
                </Radio.Group>

                <Button type="primary" block icon={<SearchOutlined />} onClick={() => { handleFilenameSearch(); setSearchModalVisible(false); }}>
                    {t('search', 'Search')}
                </Button>
            </Space>
        </div>
    );

    // Field group definitions for Search Specific Fields
    const fieldGroups = [
        {
            key: 'content_tags',
            label: t('content-and-tags', 'Content and Tags'),
            fields: [
                { key: 'ocr_content', label: t('document-text-ocr', 'Document Text/OCR') },
                { key: 'transcript', label: t('video-audio-transcript', 'Video/Audio Transcript') },
                { key: 'notes', label: t('notes', 'Notes') },
                { key: 'tag_text', label: t('keywords', 'Keywords') },
                { key: 'auto_tag_text', label: t('auto-keywords', 'Auto Keywords') },
                { key: 'person_tag_names', label: t('people', 'People') },
                { key: 'face_names', label: t('face-tags', 'Face Tags') },
                { key: 'event', label: t('event-shown', 'Event Shown') },
                { key: 'iptceventid', label: t('event-identifiers', 'Event Identifiers') },
            ]
        },
        {
            key: 'ownership_rights',
            label: t('ownership-rights', 'Ownership/Rights'),
            fields: [
                { key: 'creator_tag_text', label: t('verified-creator', 'Verified Creator') },
                { key: 'creditline', label: t('credit-line', 'Credit Line') },
                { key: 'iptcsource', label: t('source', 'Source') },
                { key: 'iptcrights', label: t('copyright-notice', 'Copyright Notice') },
                { key: 'usageterms', label: t('usage-rights', 'Usage Rights') },
                { key: 'instructions', label: t('special-instructions', 'Special Instructions') },
            ]
        },
        {
            key: 'location',
            label: t('location', 'Location'),
            fields: [
                { key: 'country', label: t('country', 'Country') },
                { key: 'state', label: t('state', 'State') },
                { key: 'city', label: t('city', 'City') },
                { key: 'sublocation', label: t('sublocation', 'Sublocation') },
            ]
        },
        {
            key: 'description',
            label: t('description', 'Description'),
            fields: [
                { key: 'description', label: t('description', 'Description') },
                { key: 'title', label: t('title', 'Title') },
                { key: 'headline', label: t('headline', 'Headline') },
                { key: 'extendeddescription', label: t('extended-description', 'Extended Description') },
                { key: 'descriptionwriter', label: t('description-writer', 'Description Writer') },
                { key: 'alttext', label: t('alternative-text', 'Alternative Text') },
                { key: 'tag_description', label: t('keyword-description', 'Keyword Description') },
                { key: 'persondescription', label: t('person-description', 'Person Description') },
                { key: 'productdescription', label: t('product-description', 'Product Description') },
            ]
        },
        {
            key: 'artwork_object',
            label: t('artwork-object-fields', 'Artwork/Object Fields'),
            fields: [
                { key: 'aotitle', label: t('artwork-name', 'Artwork Name') },
                { key: 'aocontributiondescription', label: t('art-contribution-description', 'Art Contribution Description') },
                { key: 'aocontentdescription', label: t('art-content-description', 'Art Content Description') },
                { key: 'aophysicaldescription', label: t('art-physical-description', 'Art Physical Description') },
            ]
        },
    ];

    const GroupHeader = ({group, fieldState, onToggle}) => {
        const groupKeys = group.fields.map(f => f.key);
        return (
            <div style={{display: 'flex', alignItems: 'center', marginBottom: '6px'}}>
                <Typography.Text strong style={{color: '#1890ff', fontSize: '11px', marginRight: '8px'}}>
                    {group.label}
                </Typography.Text>
                <Typography.Text style={{fontSize: '10px'}}>
                    <a onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(groupKeys, true); }}>{t('all', 'All')}</a>
                    {' / '}
                    <a onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(groupKeys, false); }}>{t('none', 'None')}</a>
                </Typography.Text>
            </div>
        );
    };

    // Field groups for Empty/Full Fields - includes captured_at and all fields from fieldGroups
    const emptyFullFieldGroups = [
        {
            key: 'content_tags',
            label: t('content-and-tags', 'Content and Tags'),
            fields: [
                ...fieldGroups[0].fields,
                { key: 'captured_at', label: t('iptc-date-created', 'IPTC Date Created') },
            ]
        },
        fieldGroups[1], // Ownership/Rights
        fieldGroups[2], // Location
        fieldGroups[3], // Description
        fieldGroups[4], // Artwork/Object
    ];

    const renderEmptyFields = () => {
        const customFieldOptions = customMetaFields.map(cmf => ({
            key: `cmf${cmf.number}`,
            label: cmf.name
        }));

        return (
            <div style={{padding: '12px 0'}}>
                <Space direction="vertical" style={{width: '100%'}} size="middle">
                    <div>
                        <Typography.Text strong style={{display: 'block', marginBottom: '8px', fontSize: '12px'}}>
                            {t('field-search-mode', 'Search Mode')}
                        </Typography.Text>
                        <Radio.Group
                            value={fieldSearchMode}
                            onChange={(e) => setFieldSearchMode(e.target.value)}
                            size="small"
                        >
                            <Radio value="empty">{t('find-empty-fields', 'Find Empty Fields')}</Radio>
                            <Radio value="full">{t('find-full-fields', 'Find Full Fields')}</Radio>
                        </Radio.Group>
                    </div>

                    <Typography.Text type="secondary" style={{fontSize: '11px'}}>
                        {fieldSearchMode === 'empty'
                            ? t('empty-fields-description', 'Select a field to find assets where that field is empty')
                            : t('full-fields-description', 'Select a field to find assets where that field has content')
                        }
                    </Typography.Text>

                    <div style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px',
                        padding: '10px',
                        maxHeight: '400px',
                        overflowY: 'auto'
                    }}>
                        <Radio.Group
                            value={selectedEmptyField}
                            onChange={(e) => setSelectedEmptyField(e.target.value)}
                            style={{width: '100%'}}
                        >
                            <div style={{columns: 2, columnGap: '16px'}}>
                                {emptyFullFieldGroups.map(group => (
                                    <div key={group.key} style={{breakInside: 'avoid', marginBottom: '12px'}}>
                                        <Typography.Text strong style={{color: '#1890ff', display: 'block', marginBottom: '6px', fontSize: '11px'}}>
                                            {group.label}
                                        </Typography.Text>
                                        <Space direction="vertical" size={2}>
                                            {group.fields.map(field => (
                                                <Radio key={field.key} value={field.key} style={{fontSize: '12px'}}>{field.label}</Radio>
                                            ))}
                                        </Space>
                                    </div>
                                ))}

                                {/* Custom Fields */}
                                {customFieldOptions.length > 0 && (
                                    <div style={{breakInside: 'avoid', marginBottom: '12px'}}>
                                        <Typography.Text strong style={{color: '#1890ff', display: 'block', marginBottom: '6px', fontSize: '11px'}}>
                                            {t('custom-fields', 'Custom Fields')}
                                        </Typography.Text>
                                        <Space direction="vertical" size={2}>
                                            {customFieldOptions.map(field => (
                                                <Radio key={field.key} value={field.key} style={{fontSize: '12px'}}>{field.label}</Radio>
                                            ))}
                                        </Space>
                                    </div>
                                )}
                            </div>
                        </Radio.Group>
                    </div>

                    <div>
                        <Typography.Text strong style={{display: 'block', marginBottom: '8px', fontSize: '12px'}}>
                            {t('search-items', 'Search Items')}
                            <HelpPopover code={'advanced-search-items'}/>
                        </Typography.Text>
                        <Radio.Group value={emptyFieldSearchScope} onChange={(e) => setEmptyFieldSearchScope(e.target.value)} size="small">
                            <Radio value="shown">{t('shown-files', 'Shown files')}</Radio>
                            <Radio value="all">{t('all-files', 'All Files')}</Radio>
                        </Radio.Group>
                    </div>

                    <Button type="primary" block icon={<SearchOutlined />} onClick={() => { handleEmptyFieldSearch(); setSearchModalVisible(false); }}>
                        {t('search', 'Search')}
                    </Button>
                </Space>
            </div>
        );
    };

    // Query Builder handlers
    const handleQbChange = _.throttle((immutableTree, config) => {
        setQbConfig(config);
        setQbTree(immutableTree);

        const jsonTree = QbUtils.getTree(immutableTree);
        setQueryValue(jsonTree);

        const sql = QbUtils.sqlFormat(immutableTree, config);
        if(sql && sql != '') {
            api.get('/api/assets/search', {params:{sql: sql}}).then(res => {
                if(res.data.error) {
                    notification.error({message:'Search Error', description: res.data.error, duration: 0})
                } else {
                    setQbResult(res.data)
                }
            })
        }
    }, 100);

    const runQbSearch = () => {
        const sql = QbUtils.sqlFormat(qbTree, qbConfig);
        if (qbSearchScope === 'all') {
            const pathParts = window.location.pathname.split('/');
            const orgSlug = pathParts[1];
            replaceFilters({sql: sql}, orgSlug ? `/${orgSlug}/explore` : null);
        } else {
            setFilters(false, {sql: sql});
        }
        setSearchModalVisible(false);
    };

    const clearQbSearch = () => {
        setQbTree(QbUtils.sanitizeTree(QbUtils.loadTree(initQuery), qbConfig).fixedTree);
        setQbResult(null);
    };

    // Saved search queries
    const saveQbSearch = () => {
        const sql = QbUtils.sqlFormat(qbTree, qbConfig);
        const json_logic = QbUtils.getTree(qbTree, qbConfig);
        const name = searchQueryNameInput.current.input.value;
        if (!name) { message.warning(t('enter-query-name', 'Enter a query name')); return; }
        api.post('/api/search_queries', {search_query: { json_logic, sql, name }}).then(() => {
            message.success(t('message-search-query-saved', 'Search Query saved!'));
            searchQueryNameInput.current.input.value = '';
            setQbSavedCount(c => c + 1);
        });
    };

    const runSavedQbSearch = (sql) => {
        setFilters(false, {sql});
        setSearchModalVisible(false);
    };

    const editQbSearch = (sq) => {
        setQbTree(QbUtils.sanitizeTree(QbUtils.loadTree(sq.json_logic), qbConfig).fixedTree);
    };

    useEffect(() => {
        if (!searchModalVisible) return;
        api('/api/search_queries').then(res => {
            setQbSearchQueries(res.data);
        });
    }, [searchModalVisible, qbSavedCount]);

    // Initialize query builder on mount
    useEffect(() => {
        handleQbChange(qbTree, qbConfig);
    }, []);

    const renderQueryBuilder = () => {
        const renderBuilder = (props) => (
            <div className="query-builder-container" style={{padding: '12px 0', overflow: 'visible'}}>
                <div className="query-builder" style={{margin: '0 0 1rem', overflow: 'visible'}}>
                    <Builder {...props} />

                    <div style={{marginTop: '12px'}}>
                        <Typography.Text strong style={{display: 'block', marginBottom: '8px', fontSize: '12px'}}>
                            {t('search-items', 'Search Items')}
                            <HelpPopover code={'advanced-search-items'}/>
                        </Typography.Text>
                        <Radio.Group value={qbSearchScope} onChange={(e) => setQbSearchScope(e.target.value)} size="small">
                            <Radio value="shown">{t('shown-files', 'Shown files')}</Radio>
                            <Radio value="all">{t('all-files', 'All Files')}</Radio>
                        </Radio.Group>
                    </div>

                    <Space direction={'horizontal'} style={{marginTop: '12px'}} wrap>
                        <Button disabled={!qbResult} type={'primary'} size="small" onClick={runQbSearch} icon={<SearchOutlined/>}>
                            {t('button-search','Search')}
                        </Button>
                        <Popconfirm
                            title={t('confirm-clear-query','Clear Query?')}
                            onConfirm={clearQbSearch}
                        >
                            <Button disabled={!qbResult} size="small" icon={<ClearOutlined/>}>
                                {t('button-reset','Reset')}
                            </Button>
                        </Popconfirm>
                        <Input ref={searchQueryNameInput} size="small" type={'text'} placeholder={t('placeholder-set-query-name...','Set Query Name...')} style={{width: 180}}/>
                        <Button disabled={!qbResult} type={'primary'} ghost size="small" onClick={saveQbSearch} icon={<SaveOutlined/>}>
                            {t('save','Save')}
                        </Button>
                    </Space>

                    {qbResult && <Alert
                        type={qbResult.total_entries > 0 ? 'success' : 'warning'}
                        style={{marginTop:'1em', paddingBottom: qbResult.total_entries > 0 ? 0 : null}}
                        showIcon
                        message={`${t('test-results', 'Test Results')}: ${qbResult.total_entries}`}
                        description={
                            <>
                                {qbResult.assets.length ?
                                    <List
                                        size="small"
                                        style={{margin:0}}
                                        grid={{}}
                                        dataSource={qbResult.assets.slice(0,10)}
                                        renderItem={asset => (
                                            <List.Item style={{marginLeft:0, padding:'0 0.5em 0 0'}}>
                                                <img src={asset.thumb_url} style={{ width: 50, height: 50, objectFit: 'scale-down'}}/>
                                            </List.Item>
                                        )}
                                    />
                                    : 'None...'}
                            </>
                        }
                    />}

                    {qbSearchQueries && qbSearchQueries.length > 0 && (
                        <List
                            header={<strong>{t('saved-searches','Saved Searches')}</strong>}
                            style={{marginTop:'1em'}}
                            size="small"
                            bordered
                            dataSource={qbSearchQueries}
                            renderItem={sq => {
                                const destroy = () => {
                                    api.delete(`/api/search_queries/${sq.id}`).then(() => {
                                        setQbSavedCount(c => c + 1);
                                        message.success(t('message-search-query-deleted.','Search Query Deleted.'));
                                    });
                                };

                                return (
                                    <List.Item style={{display: 'flex', justifyContent: 'space-between'}}>
                                        <Space direction={'horizontal'}>
                                            <Button size="small" icon={<SearchOutlined/>} onClick={() => runSavedQbSearch(sq.sql)}>{t('button-run','Run')}</Button>
                                            {sq.name}
                                            <Typography.Text code style={{fontSize: '11px'}}>{sq.sql}</Typography.Text>
                                        </Space>
                                        <Space direction={'horizontal'}>
                                            <Button size="small" icon={<EditOutlined/>} onClick={() => editQbSearch(sq)}>{t('button-edit','Edit')}</Button>
                                            <Popconfirm
                                                title={t('confirm-delete-query','Delete Query?')}
                                                onConfirm={destroy}
                                            >
                                                <Button size="small" icon={<DeleteOutlined/>} danger/>
                                            </Popconfirm>
                                        </Space>
                                    </List.Item>
                                );
                            }}
                        />
                    )}
                </div>
            </div>
        );

        return (
            <Query
                {...qbConfig}
                value={qbTree}
                onChange={handleQbChange}
                renderBuilder={renderBuilder}
            />
        );
    };

    return (
        (<GlobalHotKeys keyMap={keyMap} handlers={handlers} innerRef={ref} allowChanges>
            <search role='search' id={'site-search'} style={{display:'flex', alignItems:'center', justifyContent:'center', height:'100%', gap: '.5em'}} aria-label={t('sitewide-search', 'Sitewide Search')}>
                <ConfigProvider
                    theme={
                        {
                            components: {
                                Input: { addonBg: '#fff'} ,
                                Menu: {
                                    horizontalItemHoverBg: 'transparent',
                                    horizontalItemSelectedBg: 'transparent',
                                    itemBg: currentOrg?.header_background_color
                                },
                            }
                        }
                    }
                >
                    <div ref={searchContainerRef} style={{width: '100%', flex: 'auto'}}>
                        <AutoComplete
                            popupClassName="certain-category-search-dropdown"
                            popupMatchSelectWidth={800}
                            defaultActiveFirstOption
                            style={{
                                width: '100%',
                                borderColor: currentOrg?.header_text_color,
                                textAlign:'left',
                            }}
                            onSearch={search}
                            options={searchOptions}
                            onSelect={selectSearchOption}
                            onChange={setSearchValue}
                            value={searchValue}
                            listHeight={'80vh'}
                        >
                            <Input
                                aria-label={t('sitewide-search', 'Sitewide Search')}
                                placeholder={t('nav-search','Search...')}
                                onKeyPress={e => { if(e.key == 'Enter') selectSearchOption('all') }}
                                allowClear
                                ref={inputRef}
                                prefix={searchLoading ? <LoadingOutlined/> : <SearchOutlined style={{color: 'grey'}}/>}
                            />
                        </AutoComplete>
                    </div>

                    <Tooltip title={t('search-options', 'Search Options')}>
                        <Button
                            ref={settingsButtonRef}
                            type='text'
                            icon={<SettingOutlined style={{color: currentOrg?.header_text_color}}/>}
                            shape={'circle'}
                            aria-label={t('search-settings', 'Search Settings')}
                            id={'search-settings-btn'}
                            onClick={() => setSearchModalVisible(true)}
                        />
                    </Tooltip>

                    <Modal
                        title={<><SettingOutlined/> {t('search-options', 'Search Options')}</>}
                        open={searchModalVisible}
                        onCancel={() => setSearchModalVisible(false)}
                        footer={null}
                        width={800}
                    >
                        <Collapse
                            accordion
                            activeKey={accordionActiveKey}
                            onChange={(key) => {
                                const activeKey = Array.isArray(key) ? (key.length ? key[0] : null) : (key || null);
                                setAccordionActiveKey(activeKey);
                            }}
                            items={[
                                {
                                    key: 'options',
                                    label: <Typography.Text strong><SettingOutlined /> {t('search-options', 'Search Options')}</Typography.Text>,
                                    children: (
                                        <VerticalSpace>
                                            <Input
                                                placeholder={t('nav-search','Search...')}
                                                value={searchValue}
                                                onChange={(e) => setSearchValue(e.target.value)}
                                                onPressEnter={handleTopSearch}
                                                allowClear
                                                prefix={<SearchOutlined style={{color: 'grey'}}/>}
                                                style={{marginBottom: '8px'}}
                                            />

                                            <div>
                                                <Typography.Text strong style={{display: 'block', marginBottom: '8px'}}>
                                                    {t('search-items', 'Search Items')}
                                                    <HelpPopover code={'advanced-search-items'}/>
                                                </Typography.Text>
                                                <Radio.Group
                                                    value={textSearchScope}
                                                    onChange={(e) => setTextSearchScope(e.target.value)}
                                                    style={{marginLeft: '8px'}}
                                                >
                                                    <Radio value="shown">{t('shown-files', 'Shown files')}</Radio>
                                                    <Radio value="all">{t('all-files', 'All Files')}</Radio>
                                                </Radio.Group>
                                            </div>

                                            <div>
                                                <Typography.Text strong style={{display: 'block', marginBottom: '8px'}}>
                                                    {t('multiple-terms', 'Multiple terms')}
                                                    <HelpPopover code={'advanced-search-multiple-terms'}/>
                                                </Typography.Text>
                                                <Radio.Group
                                                    value={textSearchMode}
                                                    onChange={(e) => setTextSearchMode(e.target.value)}
                                                    style={{marginLeft: '8px'}}
                                                >
                                                    <Radio value="all">{t('has-all', 'Has All Terms')}</Radio>
                                                    <Radio value="any">{t('has-any', 'Has Any Term')}</Radio>
                                                </Radio.Group>
                                            </div>

                                            <div>
                                                <Typography.Text strong style={{display: 'block', marginBottom: '8px'}}>
                                                    {t('matching', 'Matching')}
                                                    <HelpPopover code={'advanced-search-matching'}/>
                                                </Typography.Text>
                                                <Radio.Group
                                                    value={textSearchMatching}
                                                    onChange={(e) => setTextSearchMatching(e.target.value)}
                                                    style={{marginLeft: '8px'}}
                                                >
                                                    <Radio value="contains">{t('contains-text', 'Contains')}</Radio>
                                                    <Radio value="exact">{t('exact-match', 'Exact Match')}</Radio>
                                                    <Radio value="starts">{t('starts-with', 'Starts With')}</Radio>
                                                </Radio.Group>
                                            </div>

                                            <div>
                                                <Typography.Text strong style={{display: 'block', marginBottom: '8px'}}>
                                                    {t('auto-keywords', 'Auto Keywords')}
                                                </Typography.Text>
                                                <Radio.Group
                                                    value={autoKeywordsMode}
                                                    onChange={(e) => { e.stopPropagation(); setAutoKeywordsMode(e.target.value); }}
                                                    style={{marginLeft: '8px'}}
                                                >
                                                    <Radio value="include">{t('include', 'Include')}</Radio>
                                                    <Radio value="exclude">{t('exclude', 'Exclude')}</Radio>
                                                </Radio.Group>
                                            </div>

                                            <Button
                                                type="primary"
                                                block
                                                icon={<SearchOutlined />}
                                                onClick={handleTopSearch}
                                            >
                                                {t('search', 'Search')}
                                            </Button>
                                        </VerticalSpace>
                                    )
                                },
                                {
                                    key: 'fields',
                                    label: <Typography.Text strong><CheckSquareOutlined /> {t('search-specific-fields', 'Search Specific Fields')} <HelpPopover code={'advanced-search-refine'}/></Typography.Text>,
                                    children: (
                                        <VerticalSpace>
                                            <Input
                                                placeholder={t('nav-search','Search...')}
                                                value={modalSearchValue}
                                                onChange={(e) => setModalSearchValue(e.target.value)}
                                                onPressEnter={handleModalSearch}
                                                allowClear
                                                autoFocus
                                                prefix={<SearchOutlined style={{color: 'grey'}}/>}
                                            />

                                            <Typography.Text strong style={{display: 'block', marginBottom: '8px'}}>
                                                {t('choose-fields-to-search', 'Choose Fields to Search')}
                                            </Typography.Text>

                                            <Space size="small" style={{marginBottom: '8px'}}>
                                                <Button size="small" onClick={() => toggleAllFields(true)}>
                                                    {t('select-all', 'Select All')}
                                                </Button>
                                                <Button size="small" onClick={() => toggleAllFields(false)}>
                                                    {t('select-none', 'Select None')}
                                                </Button>
                                            </Space>

                                            <div style={{
                                                border: '1px solid #d9d9d9',
                                                borderRadius: '4px',
                                                padding: '10px',
                                                maxHeight: '400px',
                                                overflowY: 'auto'
                                            }}>
                                                <div style={{columns: 2, columnGap: '16px'}}>
                                                    {fieldGroups.map(group => (
                                                        <div key={group.key} style={{breakInside: 'avoid', marginBottom: '12px'}}>
                                                            <GroupHeader group={group} fieldState={selectedFields} onToggle={toggleGroupFields} />
                                                            <Space direction="vertical" size={2}>
                                                                {group.fields.map(field => (
                                                                    <Checkbox
                                                                        key={field.key}
                                                                        checked={selectedFields[field.key]}
                                                                        onChange={() => toggleField(field.key)}
                                                                        style={{fontSize: '12px'}}
                                                                    >
                                                                        {field.label}
                                                                    </Checkbox>
                                                                ))}
                                                            </Space>
                                                        </div>
                                                    ))}

                                                    {/* Custom Fields group */}
                                                    <div style={{breakInside: 'avoid', marginBottom: '12px'}}>
                                                        <div style={{display: 'flex', alignItems: 'center', marginBottom: '6px'}}>
                                                            <Typography.Text strong style={{color: '#1890ff', fontSize: '11px', marginRight: '8px'}}>
                                                                {t('custom-fields', 'Custom Fields')}
                                                            </Typography.Text>
                                                            {customMetaFields.length > 0 && (
                                                                <Typography.Text style={{fontSize: '10px'}}>
                                                                    <a onClick={(e) => {
                                                                        e.preventDefault(); e.stopPropagation();
                                                                        const newCustomFields = {};
                                                                        customMetaFields.forEach(cmf => { newCustomFields[`cmf${cmf.number}`] = true; });
                                                                        setSelectedCustomFields(newCustomFields);
                                                                    }}>{t('all', 'All')}</a>
                                                                    {' / '}
                                                                    <a onClick={(e) => {
                                                                        e.preventDefault(); e.stopPropagation();
                                                                        const newCustomFields = {};
                                                                        customMetaFields.forEach(cmf => { newCustomFields[`cmf${cmf.number}`] = false; });
                                                                        setSelectedCustomFields(newCustomFields);
                                                                    }}>{t('none', 'None')}</a>
                                                                </Typography.Text>
                                                            )}
                                                        </div>
                                                        <Space direction="vertical" size={2}>
                                                            {customMetaFields.length > 0 ? (
                                                                customMetaFields.map(cmf => (
                                                                    <Checkbox
                                                                        key={cmf.id}
                                                                        checked={selectedCustomFields[`cmf${cmf.number}`] || false}
                                                                        onChange={() => toggleCustomField(`cmf${cmf.number}`)}
                                                                        style={{fontSize: '12px'}}
                                                                    >
                                                                        {cmf.name}
                                                                    </Checkbox>
                                                                ))
                                                            ) : (
                                                                <Typography.Text type="secondary" style={{fontSize: '11px'}}>
                                                                    {t('no-custom-fields', 'No custom fields defined')}
                                                                </Typography.Text>
                                                            )}
                                                        </Space>
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <Typography.Text strong style={{display: 'block', marginBottom: '8px'}}>
                                                    {t('search-items', 'Search Items')}
                                                    <HelpPopover code={'advanced-search-items'}/>
                                                </Typography.Text>
                                                <Radio.Group
                                                    value={specificFieldsScope}
                                                    onChange={(e) => setSpecificFieldsScope(e.target.value)}
                                                    style={{marginLeft: '8px'}}
                                                >
                                                    <Radio value="shown">{t('shown-files', 'Shown files')}</Radio>
                                                    <Radio value="all">{t('all-files', 'All Files')}</Radio>
                                                </Radio.Group>
                                            </div>

                                            <div>
                                                <Typography.Text strong style={{display: 'block', marginBottom: '8px'}}>
                                                    {t('multiple-terms', 'Multiple terms')}
                                                    <HelpPopover code={'advanced-search-multiple-terms'}/>
                                                </Typography.Text>
                                                <Radio.Group
                                                    value={specificFieldsMode}
                                                    onChange={(e) => setSpecificFieldsMode(e.target.value)}
                                                    style={{marginLeft: '8px'}}
                                                >
                                                    <Radio value="all">{t('has-all', 'Has All Terms')}</Radio>
                                                    <Radio value="any">{t('has-any', 'Has Any Term')}</Radio>
                                                </Radio.Group>
                                            </div>

                                            <Button
                                                type="primary"
                                                block
                                                icon={<SearchOutlined />}
                                                onClick={handleModalSearch}
                                                style={{marginTop: '16px'}}
                                            >
                                                {t('search', 'Search')}
                                            </Button>
                                        </VerticalSpace>
                                    )
                                },
                                {
                                    key: 'filename',
                                    label: <Typography.Text strong><FileSearchOutlined /> {t('filename-search', 'Filename Search')} <HelpPopover code={'advanced-search-filename'}/></Typography.Text>,
                                    children: renderFilenameSearch()
                                },
                                {
                                    key: 'field',
                                    label: <Typography.Text strong><FilterOutlined /> {t('find-empty-full-fields', 'Find Empty / Full Fields')} <HelpPopover code={'advanced-search-empty-full'}/></Typography.Text>,
                                    children: renderEmptyFields()
                                },
                                {
                                    key: 'query',
                                    label: <Typography.Text strong><CodeOutlined /> {t('advanced-query-builder', 'Advanced Query Builder')} <HelpPopover code={'advanced-search-query-builder'}/></Typography.Text>,
                                    children: renderQueryBuilder()
                                }
                            ]}
                        />
                    </Modal>
                </ConfigProvider>
            </search>
        </GlobalHotKeys>)
    );
}
